export type MetaBrowserEvent =
  | "PageView"
  | "QuizStart"
  | "QuizComplete"
  | "Lead"
  | "ViewContent"
  | "InitiateCheckout";

export type MetaConsent = "accepted" | "rejected" | "undecided";

const consentKey = "vessa.meta-marketing-consent.v1";
const pendingCheckoutKey = "vessa.meta-pending-checkout.v1";
const standardEvents = new Set([
  "PageView",
  "Lead",
  "ViewContent",
  "InitiateCheckout",
]);
const allowedPaths = new Set([
  "/", "/quiz", "/offer", "/welcome",
]);

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue?: unknown[]; loaded?: boolean; version?: string };
    _fbq?: Window["fbq"];
  }
}

let pixelInitialized = false;

export function getMetaConsent(): MetaConsent {
  try {
    const value = window.localStorage.getItem(consentKey);
    return value === "accepted" || value === "rejected" ? value : "undecided";
  } catch {
    return "undecided";
  }
}

export function setMetaConsent(value: Exclude<MetaConsent, "undecided">) {
  window.localStorage.setItem(consentKey, value);
  if (value === "rejected" && window.fbq) window.fbq("consent", "revoke");
  window.dispatchEvent(new CustomEvent("vessa:meta-consent", { detail: value }));
}

export async function persistMetaConsent(value: Exclude<MetaConsent, "undecided">) {
  const pendingCheckoutSessionId = value === "rejected"
    ? window.localStorage.getItem(pendingCheckoutKey)
    : null;
  const response = await fetch("/api/meta-consent", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      choice: value,
      ...(pendingCheckoutSessionId ? { pendingCheckoutSessionId } : {}),
    }),
  });
  if (!response.ok) throw new Error("Marketing preference could not be saved");
  if (value === "rejected") window.localStorage.removeItem(pendingCheckoutKey);
  setMetaConsent(value);
}

export function rememberMetaCheckout(sessionId: string | undefined) {
  if (sessionId && /^cs_(?:test|live)_[A-Za-z0-9]+$/.test(sessionId)) {
    try {
      window.localStorage.setItem(pendingCheckoutKey, sessionId);
    } catch {
      // Measurement bookkeeping must never hide an already-created Checkout.
    }
  }
}

export function initializeMetaPixel() {
  const pixelId = import.meta.env.VITE_PUBLIC_META_PIXEL_ID?.trim();
  if (getMetaConsent() !== "accepted" || pixelInitialized || !/^\d{8,30}$/.test(pixelId ?? "")) {
    return false;
  }
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue?.push(args);
  } as NonNullable<Window["fbq"]>;
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = window._fbq = fbq;
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  script.dataset.vessaMetaPixel = "true";
  document.head.append(script);
  fbq("set", "autoConfig", false, pixelId);
  fbq("init", pixelId);
  fbq("consent", "grant");
  pixelInitialized = true;
  return true;
}

export function createMetaEventId() {
  return crypto.randomUUID();
}

export async function trackMetaEvent(
  eventName: MetaBrowserEvent,
  sourcePath = window.location.pathname,
  eventId = createMetaEventId(),
) {
  if (getMetaConsent() !== "accepted" || !allowedPaths.has(sourcePath)) return undefined;
  initializeMetaPixel();
  const pixelId = import.meta.env.VITE_PUBLIC_META_PIXEL_ID?.trim();
  if (!pixelInitialized || !pixelId || !window.fbq) return undefined;
  if (standardEvents.has(eventName)) window.fbq("track", eventName, {}, { eventID: eventId });
  else window.fbq("trackCustom", eventName, {}, { eventID: eventId });
  void fetch("/api/meta-event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ eventName, eventId, sourcePath }),
    keepalive: true,
  });
  return eventId;
}

export function trackMetaBrowserOnly(
  eventName: "Lead" | "InitiateCheckout",
  eventId: string | undefined,
) {
  try {
    if (!eventId || getMetaConsent() !== "accepted") return false;
    initializeMetaPixel();
    if (!window.fbq) return false;
    window.fbq("track", eventName, {}, { eventID: eventId });
    return true;
  } catch {
    return false;
  }
}
